// StudentTestTakingPage.js - Full-page test-taking view for students
import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getMainContentColors } from "../components/MainContentArea";
import QuestionDisplay from "../QuestionDisplay";

// Responsive breakpoints
const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

/**
 * StudentTestTakingPage - Full-page test-taking screen
 * Handles three states:
 * 1. Test details/ready to start (testReadyToStart && !showQuiz)
 * 2. Active test taking (showQuiz && quizMode === "test")
 * 3. Past test results viewing (showQuiz && quizMode === "pastTestView")
 */
const StudentTestTakingPage = ({
  // Test state
  activeTest,
  testReadyToStart,
  testTimeLeft,
  showQuiz,
  quizMode,
  quizData,
  // Answer state
  userAnswers,
  answerFeedback,
  showHints,
  showSolutions,
  // Handlers
  handleStartTest,
  handleSubmitTest,
  onClose,
  toggleHint,
  toggleSolution,
  handleAnswerChange,
  handleMCQSelection,
  handleMultiSelect, // AIT-297: multi-select toggle (question_type 4)
  checkTextAnswer,
  mapQuizModeToDisplayMode,
  // Formatting
  formatLocalDate,
  // Theme
  isDarkMode = false,
  t = (s) => s,
  // AIT-249: pass-through teacher toggle. Tests default to deferred (false);
  // pastTestView always reveals correctness via isReviewMode.
  immediateFeedback = false,
  onAskAI = null,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const isTablet = width >= TABLET_BREAKPOINT && width < DESKTOP_BREAKPOINT;

  const colors = getMainContentColors(isDarkMode);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (quizMode === "pastTestView" && showQuiz) {
      scrollRef.current?.scrollTo({ y: 0, animated: true });
    }
  }, [quizMode, showQuiz]);

  // // Practice tests are always untimed (test_kind="practice"; owner_email is a
  // // robust fallback for rows that predate that flag). No timer, no countdown.
  // const isPractice =
  //   activeTest?.test_kind === "practice" || !!activeTest?.owner_email;

  // // The countdown header badge is shown only for timed (non-practice) tests.
  // const showTimer = quizMode === "test" && showQuiz && !isPractice;
  // BDD 08/03/2026 - Journey
  const isPractice =
    activeTest?.test_kind === "practice" || !!activeTest?.owner_email;

  const isAssessment =
    activeTest?.test_kind === "assessment";

  const isUntimed =
    isPractice ||
    isAssessment ||
    !Number(activeTest?.duration_minutes);

  const showTimer =
    quizMode === "test" && showQuiz && !isUntimed;

  // Format time remaining
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  // Timer color based on time remaining
  const getTimerColor = () => {
    if (testTimeLeft < 60) return "#ef4444"; // Red when < 1 minute
    if (testTimeLeft < 300) return "#f59e0b"; // Orange when < 5 minutes
    return isDarkMode ? "#3b82f6" : "#2563eb"; // Blue otherwise
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Fixed Header with Timer */}
      <View style={[
        styles.header,
        {
          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
          borderBottomColor: isDarkMode ? "#334155" : "#e2e8f0"
        }
      ]}>
        {/* Back Button - Hidden during active test to prevent leaving */}
        {!(showQuiz && quizMode === "test") ? (
          <TouchableOpacity
            onPress={onClose}
            style={[
              styles.backButton,
              { backgroundColor: isDarkMode ? "#0f172a" : "#f1f5f9" }
            ]}
          >
            <MaterialCommunityIcons
              name="arrow-left"
              size={20}
              color={isDarkMode ? "#94a3b8" : "#64748b"}
            />
            <Text style={[styles.backButtonText, { color: isDarkMode ? "#94a3b8" : "#64748b" }]}>
              {t("Back to Tests")}
            </Text>
          </TouchableOpacity>
        ) : (
          <View style={[
            styles.testInProgressBadge,
            { backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.1)" }
          ]}>
            <MaterialCommunityIcons
              name="lock"
              size={16}
              color={isDarkMode ? "#f87171" : "#dc2626"}
            />
            <Text style={[styles.testInProgressText, { color: isDarkMode ? "#f87171" : "#dc2626" }]}>
              {t("Test in Progress")}
            </Text>
          </View>
        )}

        {/* Title */}
        <View style={styles.headerCenter}>
          <Text
            style={[styles.headerTitle, { color: colors.textPrimary }]}
            numberOfLines={1}
          >
            {activeTest?.title || t("Test")}
          </Text>
        </View>

        {/* Timer (only during active timed test — practice tests are untimed) */}
        {showTimer && (
          <View style={[styles.timerBadge, { backgroundColor: getTimerColor() }]}>
            <MaterialCommunityIcons name="clock-outline" size={18} color="#fff" />
            <Text style={styles.timerText}>
              {formatTime(testTimeLeft)}
            </Text>
          </View>
        )}

        {/* Spacer when no timer — keeps the title centered */}
        {!showTimer && <View style={{ width: 100 }} />}
      </View>

      {/* Content */}
      <ScrollView
        ref={scrollRef}
        style={styles.scrollContainer}
        contentContainerStyle={[
          styles.contentContainer,
          isDesktop && styles.contentContainerDesktop
        ]}
      >
        {/* Test Ready to Start - Show Details */}
        {testReadyToStart && activeTest && quizMode === "test" && !showQuiz ? (
          <View style={[
            styles.detailsCard,
            { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }
          ]}>
            {/* Test Icon */}
            <View style={[
              styles.testIconContainer,
              { backgroundColor: isDarkMode ? "rgba(99, 102, 241, 0.2)" : "rgba(99, 102, 241, 0.1)" }
            ]}>
              <MaterialCommunityIcons
                name="clipboard-text-outline"
                size={48}
                color={isDarkMode ? "#818cf8" : "#6366f1"}
              />
            </View>

            {/* Test Title */}
            <Text style={[styles.detailsTitle, { color: colors.textPrimary }]}>
              {activeTest.title}
            </Text>

            {/* Test Info Grid */}
            <View style={styles.infoGrid}>
              {activeTest.teacher_email ? (
              <View style={[
                styles.infoItem,
                { backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc" }
              ]}>
                <MaterialCommunityIcons
                  name="account-outline"
                  size={22}
                  color={isDarkMode ? "#60a5fa" : "#3b82f6"}
                />
                <View style={styles.infoTextContainer}>
                  <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
                    {t("Teacher")}
                  </Text>
                  <Text style={[styles.infoValue, { color: colors.textPrimary }]}>
                    {activeTest.teacher_email}
                  </Text>
                </View>
              </View>
              ) : null}

              {activeTest.duration_minutes ? (
              <View style={[
                styles.infoItem,
                { backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc" }
              ]}>
                <MaterialCommunityIcons
                  name="clock-outline"
                  size={22}
                  color={isDarkMode ? "#f59e0b" : "#d97706"}
                />
                <View style={styles.infoTextContainer}>
                  <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
                    {t("Duration")}
                  </Text>
                  <Text style={[styles.infoValue, { color: colors.textPrimary }]}>
                    {activeTest.duration_minutes} {t("minutes")}
                  </Text>
                </View>
              </View>
              ) : null}

              {(activeTest.start_time || activeTest.end_time) ? (
              <View style={[
                styles.infoItem,
                { backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc" }
              ]}>
                <MaterialCommunityIcons
                  name="calendar-range"
                  size={22}
                  color={isDarkMode ? "#34d399" : "#10b981"}
                />
                <View style={styles.infoTextContainer}>
                  <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
                    {t("Available")}
                  </Text>
                  <Text style={[styles.infoValue, { color: colors.textPrimary }]} numberOfLines={2}>
                    {activeTest.start_time ? formatLocalDate(activeTest.start_time) : "N/A"} – {activeTest.end_time ? formatLocalDate(activeTest.end_time) : "N/A"}
                  </Text>
                </View>
              </View>
              ) : null}

              {activeTest.description && (
                <View style={[
                  styles.infoItem,
                  styles.infoItemFull,
                  { backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc" }
                ]}>
                  <MaterialCommunityIcons
                    name="text-box-outline"
                    size={22}
                    color={isDarkMode ? "#a78bfa" : "#8b5cf6"}
                  />
                  <View style={styles.infoTextContainer}>
                    <Text style={[styles.infoLabel, { color: colors.textSecondary }]}>
                      {t("Description")}
                    </Text>
                    <Text style={[styles.infoValue, { color: colors.textPrimary }]}>
                      {activeTest.description}
                    </Text>
                  </View>
                </View>
              )}
            </View>

            {/* Chapters (if any) */}
            {activeTest.chapters?.length > 0 && (
              <View style={[
                styles.chaptersSection,
                { borderTopColor: isDarkMode ? "#334155" : "#e2e8f0" }
              ]}>
                <Text style={[styles.chaptersSectionTitle, { color: colors.textPrimary }]}>
                  <MaterialCommunityIcons
                    name="book-open-variant"
                    size={18}
                    color={isDarkMode ? "#60a5fa" : "#3b82f6"}
                  />{" "}
                  {t("Covered Chapters")}
                </Text>
                {activeTest.chapters.map((ch, idx) => {
                  const cname = (ch?.chapter_name || ch?.name || "").trim();
                  return (
                    <Text key={idx} style={[styles.chapterItem, { color: colors.textSecondary }]}>
                      {"\u2022"} {cname ? cname : `Chapter ID: ${ch?.chapter_id}`}
                    </Text>
                  );
                })}
              </View>
            )}

            {/* Start Test Button */}
            <TouchableOpacity
              onPress={handleStartTest}
              style={styles.startTestButton}
              activeOpacity={0.8}
            >
              <MaterialCommunityIcons name="play-circle" size={24} color="#fff" />
              <Text style={styles.startTestButtonText}>
                {t("Start Test")}
              </Text>
            </TouchableOpacity>

            {/* Warning */}
            <View style={[
              styles.warningBox,
              { backgroundColor: isDarkMode ? "rgba(251, 191, 36, 0.1)" : "rgba(251, 191, 36, 0.08)" }
            ]}>
              <MaterialCommunityIcons
                name="alert-circle-outline"
                size={20}
                color={isDarkMode ? "#fbbf24" : "#d97706"}
              />
              <Text style={[styles.warningText, { color: isDarkMode ? "#fbbf24" : "#d97706" }]}>
                  {isAssessment
                    ? t("This is a short, untimed science check-in. Take your time and choose your best answer.")
                    : isPractice
                      ? t("This is an untimed practice test. Take your time and answer at your own pace.")
                      : t("Once you start, the timer will begin. Make sure you have enough time to complete the test.")}
              </Text>
            </View>
          </View>
        ) : showQuiz ? (
          /* Test Questions Display */
          <View style={[
            styles.questionsCard,
            { backgroundColor: "#fff" } // Force light – KaTeX equations need light backgrounds
          ]}>
            {/* Section Header */}
            <View style={[
              styles.questionsHeader,
              { borderBottomColor: "#e2e8f0" }
            ]}>
              <View style={styles.questionsHeaderLeft}>
                <MaterialCommunityIcons
                  name={quizMode === "test" ? "clipboard-list" : "clipboard-check-outline"}
                  size={24}
                  color={quizMode === "test" ? "#3b82f6" : "#10b981"}
                />
                <Text style={[styles.questionsHeaderTitle, { color: "#0f172a" }]}>
                  {quizMode === "test" ? t("Test Questions") : t("Test Results")}
                </Text>
              </View>
              {quizData?.length > 0 && (
                <View style={[
                  styles.questionCountBadge,
                  { backgroundColor: "#f1f5f9" }
                ]}>
                  <Text style={[styles.questionCountText, { color: "#64748b" }]}>
                    {quizData.length} {t("questions")}
                  </Text>
                </View>
              )}
            </View>

            {/* Questions */}
            <QuestionDisplay
              mode={mapQuizModeToDisplayMode(quizMode)}
              quiz={quizData}
              isDarkMode={false} // Force light mode – KaTeX equations render with black text
              userAnswers={userAnswers}
              answerFeedback={answerFeedback}
              showHints={showHints}
              showSolutions={showSolutions}
              toggleHint={toggleHint}
              toggleSolution={toggleSolution}
              handleAnswerChange={handleAnswerChange}
              handleMCQSelection={handleMCQSelection}
              handleMultiSelect={handleMultiSelect}
              checkTextAnswer={checkTextAnswer}
              // AIT-249: drive deferred-feedback gating + review reveal
              immediateFeedback={immediateFeedback}
              isReviewMode={quizMode === "pastTestView"}
              onAskAI={onAskAI}              
            />

            {/* Submit Button (only for active test) */}
            {quizMode === "test" && (
              <View style={styles.submitSection}>
                <TouchableOpacity
                  onPress={handleSubmitTest}
                  style={styles.submitButton}
                  activeOpacity={0.8}
                >
                  <MaterialCommunityIcons name="check-circle" size={24} color="#fff" />
                  <Text style={styles.submitButtonText}>
                    {t("Submit Test")}
                  </Text>
                </TouchableOpacity>
                <Text style={[styles.submitHint, { color: "#64748b" }]}>
                  {t("Make sure you've answered all questions before submitting.")}
                </Text>
              </View>
            )}
          </View>
        ) : null}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 8,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: "600",
  },
  testInProgressBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  testInProgressText: {
    fontSize: 13,
    fontWeight: "600",
  },
  headerCenter: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  timerBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    gap: 6,
  },
  timerText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
  },
  scrollContainer: {
    flex: 1,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 100,
  },
  contentContainerDesktop: {
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  detailsCard: {
    borderRadius: 16,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  testIconContainer: {
    width: 88,
    height: 88,
    borderRadius: 24,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
    marginBottom: 20,
  },
  detailsTitle: {
    fontSize: 26,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: 24,
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 20,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 12,
    width: "48%",
    minWidth: 200,
    flex: 1,
    gap: 12,
  },
  infoItemFull: {
    width: "100%",
    flex: undefined,
  },
  infoTextContainer: {
    flex: 1,
  },
  infoLabel: {
    fontSize: 12,
    fontWeight: "500",
    marginBottom: 4,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: "600",
  },
  chaptersSection: {
    paddingTop: 20,
    marginTop: 8,
    borderTopWidth: 1,
    marginBottom: 20,
  },
  chaptersSectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 12,
  },
  chapterItem: {
    fontSize: 14,
    marginLeft: 8,
    marginBottom: 6,
  },
  startTestButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3b82f6",
    paddingVertical: 16,
    borderRadius: 12,
    gap: 10,
    marginBottom: 16,
  },
  startTestButtonText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
  },
  warningBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 10,
    gap: 10,
  },
  warningText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  questionsCard: {
    borderRadius: 16,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 3,
  },
  questionsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 16,
    marginBottom: 16,
    borderBottomWidth: 1,
  },
  questionsHeaderLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  questionsHeaderTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  questionCountBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  questionCountText: {
    fontSize: 13,
    fontWeight: "500",
  },
  submitSection: {
    marginTop: 24,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: "rgba(0,0,0,0.1)",
  },
  submitButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#22c55e",
    paddingVertical: 16,
    borderRadius: 12,
    gap: 10,
    marginBottom: 12,
  },
  submitButtonText: {
    color: "#fff",
    fontSize: 18,
    fontWeight: "700",
  },
  submitHint: {
    fontSize: 13,
    textAlign: "center",
  },
});

export default StudentTestTakingPage;
